import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Synchronous version for use in server-side contexts
 * Uses the MSCI ACWI country-region mapping with fallback logic
 */
export function getRegionFromExchange(exchange?: string, country?: string): string {
  if (!exchange && !country) return 'Others'
  
  const countryStr = country?.toLowerCase() || ''
  
  // Direct country mapping using MSCI ACWI classification
  const countryMappings: Record<string, string> = {
    // North America
    'united states': 'North America', 'usa': 'North America', 'us': 'North America',
    'canada': 'North America', 'can': 'North America',
    
    // Europe (Developed)
    'united kingdom': 'Europe', 'gbr': 'Europe', 'gb': 'Europe',
    'germany': 'Europe', 'deu': 'Europe', 'de': 'Europe',
    'france': 'Europe', 'fra': 'Europe', 'fr': 'Europe',
    'switzerland': 'Europe', 'che': 'Europe',
    'netherlands': 'Europe', 'nld': 'Europe', 'nl': 'Europe',
    'italy': 'Europe', 'ita': 'Europe',
    'spain': 'Europe', 'esp': 'Europe',
    'sweden': 'Europe', 'swe': 'Europe',
    'denmark': 'Europe', 'dnk': 'Europe',
    'norway': 'Europe', 'nor': 'Europe',
    'finland': 'Europe', 'fin': 'Europe',
    'belgium': 'Europe', 'bel': 'Europe',
    'austria': 'Europe', 'aut': 'Europe',
    'ireland': 'Europe', 'irl': 'Europe',
    'portugal': 'Europe', 'prt': 'Europe',
    'israel': 'Europe', 'isr': 'Europe',
    
    // Asia (Developed)
    'japan': 'Asia', 'jpn': 'Asia', 'jp': 'Asia',
    'australia': 'Asia', 'aus': 'Asia',
    'hong kong': 'Asia', 'hkg': 'Asia', 'hk': 'Asia',
    'singapore': 'Asia', 'sgp': 'Asia',
    'new zealand': 'Asia', 'nzl': 'Asia',
    
    // EM Asia
    'china': 'EM Asia', 'chn': 'EM Asia', 'cn': 'EM Asia',
    'south korea': 'EM Asia', 'kor': 'EM Asia', 'kr': 'EM Asia',
    'taiwan': 'EM Asia', 'twn': 'EM Asia', 'tw': 'EM Asia',
    'india': 'EM Asia', 'ind': 'EM Asia',
    'indonesia': 'EM Asia', 'idn': 'EM Asia',
    'thailand': 'EM Asia', 'tha': 'EM Asia',
    'malaysia': 'EM Asia', 'mys': 'EM Asia',
    'philippines': 'EM Asia', 'phl': 'EM Asia',
    'vietnam': 'EM Asia', 'vnm': 'EM Asia',
    'pakistan': 'EM Asia', 'pak': 'EM Asia',
    'bangladesh': 'EM Asia', 'bgd': 'EM Asia',
    'sri lanka': 'EM Asia', 'lka': 'EM Asia',
    
    // LATAM
    'brazil': 'LATAM', 'bra': 'LATAM',
    'mexico': 'LATAM', 'mex': 'LATAM',
    'chile': 'LATAM', 'chl': 'LATAM',
    'peru': 'LATAM', 'per': 'LATAM',
    'colombia': 'LATAM', 'col': 'LATAM',
    'argentina': 'LATAM', 'arg': 'LATAM',
    'uruguay': 'LATAM', 'ury': 'LATAM',
    
    // Eastern Europe
    'poland': 'Eastern Europe', 'pol': 'Eastern Europe',
    'czech republic': 'Eastern Europe', 'cze': 'Eastern Europe',
    'hungary': 'Eastern Europe', 'hun': 'Eastern Europe',
    'russia': 'Eastern Europe', 'rus': 'Eastern Europe',
    'turkey': 'Eastern Europe', 'tur': 'Eastern Europe',
    'greece': 'Eastern Europe', 'grc': 'Eastern Europe',
    
    // Others (Middle East & Africa)
    'saudi arabia': 'Others', 'sau': 'Others',
    'united arab emirates': 'Others', 'are': 'Others',
    'qatar': 'Others', 'qat': 'Others',
    'kuwait': 'Others', 'kwt': 'Others',
    'egypt': 'Others', 'egy': 'Others',
    'south africa': 'Others', 'zaf': 'Others',
    'morocco': 'Others', 'mar': 'Others',
    'kenya': 'Others', 'ken': 'Others',
    'nigeria': 'Others', 'nga': 'Others',
  }
  
  // First, try direct country mapping
  if (countryStr && countryMappings[countryStr]) {
    return countryMappings[countryStr]
  }
  
  // Fallback to exchange-based mapping
  const exchangeStr = exchange?.toLowerCase() || ''
  
  // US Markets
  if (exchangeStr.includes('nasdaq') || exchangeStr.includes('nyse') || 
      exchangeStr.includes('new york')) {
    return 'North America'
  }
  
  // Canadian Markets
  if (exchangeStr.includes('toronto') || exchangeStr.includes('tsx')) {
    return 'North America'
  }
  
  // European Markets
  if (exchangeStr.includes('london') || exchangeStr.includes('euronext') || 
      exchangeStr.includes('frankfurt') || exchangeStr.includes('amsterdam') ||
      exchangeStr.includes('xetra') || exchangeStr.includes('milan') ||
      exchangeStr.includes('madrid') || exchangeStr.includes('stockholm')) {
    return 'Europe'
  }
  
  // Asian Markets (Developed)
  if (exchangeStr.includes('tokyo') || exchangeStr.includes('fukuoka') ||
      exchangeStr.includes('singapore') || exchangeStr.includes('sydney') ||
      exchangeStr.includes('asx')) {
    return 'Asia'
  }
  
  // EM Asian Markets
  if (exchangeStr.includes('hong kong') || exchangeStr.includes('shanghai') || 
      exchangeStr.includes('shenzhen') || exchangeStr.includes('korea') ||
      exchangeStr.includes('taiwan') || exchangeStr.includes('taipei')) {
    return 'EM Asia'
  }
  
  return 'Others'
}